"use client";

import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AttachmentUploader } from "@/components/features/dao/election/AttachmentUploader";
import { Form } from "@/components/forms";
import { Button, Field, Label } from "@/components/ui";
import { RHFInput, RHFTextarea } from "@/components/ui/hook-form";
import { Skeleton } from "@/components/ui/skeleton";
import { useCreateStatement } from "@/hooks/statement/useCreateStatement";
import { useGetStatementDraft } from "@/hooks/statement/useGetStatementDraft";
import { usePublishStatementDraft } from "@/hooks/statement/usePublishStatementDraft";
import { useSaveStatementDraft } from "@/hooks/statement/useSaveStatementDraft";
import { useUpdateStatementDraft } from "@/hooks/statement/useUpdateStatementDraft";
import { useRouter } from "@/i18n/navigation";
import {
  makeStatementFormSchema,
  type StatementFormValues,
} from "@/lib/validations/statement/statementForm";

const EMPTY: StatementFormValues = {
  title: "",
  summary: "",
  problem: "",
  solution: "",
  impact: "",
  budget: "",
  timing: "",
  attachment_urls: [],
};

interface DraftShape {
  id: string;
  title: string | null;
  summary: string | null;
  problem: string | null;
  solution: string | null;
  impact: string | null;
  budget: string | null;
  timing: string | null;
  attachment_url: string | null;
}

const buildBody = (data: StatementFormValues) => ({
  title: data.title,
  summary: data.summary,
  problem: data.problem,
  solution: data.solution,
  impact: data.impact,
  budget: data.budget || undefined,
  timing: data.timing || undefined,
  attachment_url: data.attachment_urls[0] || undefined,
});

const RequiredLabel: React.FC<{
  htmlFor: string;
  children: React.ReactNode;
}> = ({ htmlFor, children }) => (
  <Label htmlFor={htmlFor} className="gap-0.5">
    <span>{children}</span>
    <span
      className="text-semantic-error"
      style={{ fontFamily: "var(--font-inter), system-ui, arial" }}
    >
      *
    </span>
  </Label>
);

/**
 * New / draft statement form — all 8 statement fields + optional attachment.
 * Two actions: «ذخیره پیش نویس» (save draft, no validation) and «انتشار بیانیه»
 * (publish, validated). When `draftId` is set the form loads that draft and the
 * actions PATCH / publish it instead of creating a new row.
 *
 * The whole form is gated on `eligible`: until the user runs the DNM balance
 * check and has at least one DNM, every field and action stays disabled.
 */
export const NewStatementForm: React.FC<{
  draftId?: string;
  /** Gate — false until the balance check confirms the user holds ≥1 DNM. */
  eligible: boolean;
}> = ({ draftId, eligible }) => {
  const t = useTranslations("statement");
  const vt = useTranslations("validation");
  const schema = useMemo(() => makeStatementFormSchema(vt), [vt]);
  const router = useRouter();

  const create = useCreateStatement();
  const saveDraft = useSaveStatementDraft();
  const updateDraft = useUpdateStatementDraft();
  const publishDraft = usePublishStatementDraft();

  const draftQuery = useGetStatementDraft(draftId);
  const draft = (
    draftQuery.data as unknown as { data?: DraftShape } | undefined
  )?.data;

  const [isUploading, setIsUploading] = useState(false);
  const isPending =
    create.isPending ||
    saveDraft.isPending ||
    updateDraft.isPending ||
    publishDraft.isPending;

  // Draft id present but still loading → hold the form so we prefill once.
  if (draftId && draftQuery.isLoading) {
    return <Skeleton variant="default" className="h-96 rounded-md" />;
  }

  const defaults: StatementFormValues = draft
    ? {
        title: draft.title ?? "",
        summary: draft.summary ?? "",
        problem: draft.problem ?? "",
        solution: draft.solution ?? "",
        impact: draft.impact ?? "",
        budget: draft.budget ?? "",
        timing: draft.timing ?? "",
        attachment_urls: draft.attachment_url ? [draft.attachment_url] : [],
      }
    : EMPTY;
  // Remount the form once the draft resolves so RHF picks up the prefill.
  const formKey = draft ? `draft-${draft.id}` : "new";

  const goToMine = () => router.push("/info/statement?tab=mine");

  const onPublish = async (data: StatementFormValues) => {
    try {
      if (draftId) {
        await updateDraft.mutateAsync({
          params: { path: { id: draftId } },
          body: buildBody(data),
        });
        await publishDraft.mutateAsync({ params: { path: { id: draftId } } });
      } else {
        await create.mutateAsync({ body: buildBody(data) });
      }
      goToMine();
    } catch {
      toast.error(t("form.submitError"));
    }
  };

  const onSaveDraft = async (data: StatementFormValues) => {
    try {
      if (draftId) {
        await updateDraft.mutateAsync({
          params: { path: { id: draftId } },
          body: buildBody(data),
        });
      } else {
        await saveDraft.mutateAsync({ body: buildBody(data) });
      }
      toast.success(t("form.draftSaved"));
      goToMine();
    } catch {
      toast.error(t("form.draftError"));
    }
  };

  return (
    <Form
      key={formKey}
      schema={schema}
      defaultValues={defaults}
      mode="onChange"
      onSubmit={onPublish}
    >
      {(form) => (
        // A disabled <fieldset> natively locks every nested input + button —
        // including the AttachmentUploader — until the balance check passes.
        <fieldset disabled={!eligible || isPending} className="space-y-4">
          <Field>
            <RequiredLabel htmlFor="field-title">
              {t("form.title")}
            </RequiredLabel>
            <RHFInput
              id="field-title"
              name="title"
              placeholder={t("form.titlePlaceholder")}
              disabled={isPending}
            />
          </Field>

          <Field>
            <RequiredLabel htmlFor="field-summary">
              {t("form.summary")}
            </RequiredLabel>
            <RHFTextarea
              id="field-summary"
              name="summary"
              placeholder={t("form.summaryPlaceholder")}
              className="min-h-[80px] resize-none"
              disabled={isPending}
            />
          </Field>

          <Field>
            <RequiredLabel htmlFor="field-problem">
              {t("form.problem")}
            </RequiredLabel>
            <RHFTextarea
              id="field-problem"
              name="problem"
              placeholder={t("form.problemPlaceholder")}
              className="min-h-[80px] resize-none"
              disabled={isPending}
            />
          </Field>

          <Field>
            <RequiredLabel htmlFor="field-solution">
              {t("form.solution")}
            </RequiredLabel>
            <RHFTextarea
              id="field-solution"
              name="solution"
              placeholder={t("form.solutionPlaceholder")}
              className="min-h-[80px] resize-none"
              disabled={isPending}
            />
          </Field>

          <Field>
            <Label htmlFor="field-budget">
              <span>{t("form.budget")}</span>
            </Label>
            <RHFTextarea
              id="field-budget"
              name="budget"
              placeholder={t("form.budgetPlaceholder")}
              className="min-h-[80px] resize-none"
              disabled={isPending}
            />
          </Field>

          <Field>
            <RequiredLabel htmlFor="field-impact">
              {t("form.impact")}
            </RequiredLabel>
            <RHFTextarea
              id="field-impact"
              name="impact"
              placeholder={t("form.impactPlaceholder")}
              className="min-h-[80px] resize-none"
              disabled={isPending}
            />
          </Field>

          <Field>
            <Label htmlFor="field-timing">
              <span>{t("form.timing")}</span>
            </Label>
            <RHFTextarea
              id="field-timing"
              name="timing"
              placeholder={t("form.timingPlaceholder")}
              className="min-h-[80px] resize-none"
              disabled={isPending}
            />
          </Field>

          {/* Attachment row — «فایل ضمیمه» + white «بارگذاری» */}
          <AttachmentUploader
            label={t("form.attachment")}
            uploadButtonColors="white"
            onChange={(urls) => {
              form.setValue("attachment_urls", urls, { shouldValidate: true });
            }}
            onUploadingChange={setIsUploading}
          />

          {/* Actions mirror the item-creation form (ItemForms final step):
              save draft = faded/secondary, publish = solid/secondary (white).
              Both disabled while the form is invalid. */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="faded"
              colors="secondary"
              className="flex-1"
              loading={saveDraft.isPending || updateDraft.isPending}
              disabled={isPending || isUploading || !form.formState.isValid}
              onClick={() => onSaveDraft(form.getValues())}
            >
              {t("form.saveDraft")}
            </Button>
            <Button
              type="submit"
              variant="solid"
              colors="secondary"
              className="flex-1"
              loading={create.isPending || publishDraft.isPending}
              disabled={isPending || isUploading || !form.formState.isValid}
            >
              {t("form.submit")}
            </Button>
          </div>
        </fieldset>
      )}
    </Form>
  );
};
